import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useHeroSnackbar,
  useTable,
  useQueryTableFetch,
  useTranslations,
  useTableLocalization,
  useFilterButton,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IUseFilterButtonReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
  TSecurityScoreName,
  getSecurityScoreVaultsStrength,
} from '@uniqkey-frontend/shared-app';
import {
  VaultPasswordsOrderPropertyV2,
} from '@uniqkey-backend-partner/api-client';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  IGetOrganizationEmployeeAccountVaultPasswordsV2Params,
  TGetOrganizationEmployeeAccountVaultPasswordsV2Method,
} from '../../useEmployeeAccountsAPI/interfaces';
import {
  IOrganizationEmployeeLoginsTableRow,
} from '../../../components/tables/OrganizationEmployeeLoginsTable';
import useEmployeeAccountsAPI from '../../useEmployeeAccountsAPI';
import tablePreservationConfigs from '../../../helpers/tablePreservation/configs';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
// eslint-disable-next-line max-len
import { IOrganizationEmployeeLoginsTabFilterSubmitResult } from '../../../pages/OrganizationEmployeePage/components/OrganizationEmployeeLoginsTab/components/OrganizationEmployeeLoginsTabFilter';

export const REACT_QUERY_ORGANIZATION_EMPLOYEE_LOGINS_KEY = [
  ReactQueryKeyEnum.OrganizationEmployeeLogins,
];

interface IPersistentFilters extends Pick<
  IGetOrganizationEmployeeAccountVaultPasswordsV2Params, 'employeeAccountId'
> {}

export interface IUseOrganizationEmployeeLoginsTableParams {
  persistentFilters: IPersistentFilters;
  noDataMessageKey: string;
}

export interface IUseOrganizationEmployeeLoginsTableReturn extends
  Omit<IUseTableReturn<IOrganizationEmployeeLoginsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage'>,
  Omit<
    IUseQueryTableFetchReturn<TGetOrganizationEmployeeAccountVaultPasswordsV2Method>,
    'data' | 'isFetchedAfterMount'
  >,
  IUseFilterButtonReturn
{
  logins: IUseQueryTableFetchReturn<TGetOrganizationEmployeeAccountVaultPasswordsV2Method>['data'];
  selectedLogins: IUseTableReturn<IOrganizationEmployeeLoginsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetOrganizationEmployeeAccountVaultPasswordsV2Params['searchQuery'];
  setSearchQuery: Dispatch<
    SetStateAction<IGetOrganizationEmployeeAccountVaultPasswordsV2Params['searchQuery']>
  >;
  filterValues: IOrganizationEmployeeLoginsTabFilterSubmitResult;
  setFilterValues: Dispatch<SetStateAction<IOrganizationEmployeeLoginsTabFilterSubmitResult>>;
}

interface ITablePreservationConfigs extends
  Omit<IGetOrganizationEmployeeAccountVaultPasswordsV2Params,
    'page' | 'pageLength' | 'groupId' | 'employeeAccountId' |
    'applicationName' | 'minStrength' | 'maxStrength' |
    'minSecurityScore' | 'maxSecurityScore' | 'orderPropertyName' | 'isDescending' |
    'isPasswordEmpty' | 'password2FaStatus' | 'isReusedByUser' | 'isReusedByOrganization'
  >,
  Pick<
    IUseTableReturn<IOrganizationEmployeeLoginsTableRow
    >, 'columnOrderBy' | 'columnOrderDirection'>
{
  activePage: IGetOrganizationEmployeeAccountVaultPasswordsV2Params['page'];
  perPage: IGetOrganizationEmployeeAccountVaultPasswordsV2Params['pageLength'];
  securityScoreName: TSecurityScoreName;
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: VaultPasswordsOrderPropertyV2 },
  ),
  columnOrderDirection: true,
  securityScoreName: true,
  ownership: tablePreservationConfigs.ownership,
};

const useOrganizationEmployeeLoginsTable = (
  params: IUseOrganizationEmployeeLoginsTableParams,
): IUseOrganizationEmployeeLoginsTableReturn => {
  const {
    persistentFilters,
    noDataMessageKey,
  } = params;

  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getOrganizationEmployeeAccountVaultPasswordsV2 } = useEmployeeAccountsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    searchQuery: initialSearchQuery,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
    securityScoreName: initialSecurityScoreName,
    ownership: initialOwnership,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [searchQuery, setSearchQuery] = useState<
    IGetOrganizationEmployeeAccountVaultPasswordsV2Params['searchQuery']
  >(() => initialSearchQuery);
  const [filterValues, setFilterValues] = useState<
    IOrganizationEmployeeLoginsTabFilterSubmitResult
  >({
    ownership: initialOwnership,
    securityScoreName: initialSecurityScoreName,
  });

  const { isFilterActive, numberOfActiveFilters } = useFilterButton(filterValues);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedLogins,
    resetTableMethods,
    ...restTableProps
  } = useTable<IOrganizationEmployeeLoginsTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
    setFilterValues({
      ownership: undefined,
      securityScoreName: TablePreserverCommonInitialTableValues.securityScoreName,
    });
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: logins, isLoading, isFetchedAfterMount, total, resetQuery,
  } = useQueryTableFetch({
    queryKey: REACT_QUERY_ORGANIZATION_EMPLOYEE_LOGINS_KEY,
    queryClient,
    request: getOrganizationEmployeeAccountVaultPasswordsV2,
    params: {
      page: activePage,
      pageLength: perPage,
      ownership: filterValues.ownership,
      ...getSecurityScoreVaultsStrength(filterValues.securityScoreName),
      searchQuery,
      orderPropertyName: VaultPasswordsOrderPropertyV2[
        upperFirst(columnOrderBy) as keyof typeof VaultPasswordsOrderPropertyV2
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
    autoRefetch: true,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isFilterActive,
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
    ownership: filterValues.ownership,
    securityScoreName: filterValues.securityScoreName,
  });

  return {
    logins,
    isLoading,
    total,
    resetQuery,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedLogins,
    localization,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    ...restTableProps,
  };
};

export default useOrganizationEmployeeLoginsTable;
